import React from "react";
import { graphql, useFragment } from "react-relay";

import { showFeatureModal } from ".";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon, type FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { smart } from "~/lib/relayUtil";

import type { FeatureValues } from "./utils";

import type {
	BillingIdentityFeaturesPaywall_viewer$key,
	BillingIdentityFeaturesPaywall_viewer$data as Viewer,
} from "~/__generated__/BillingIdentityFeaturesPaywall_viewer.graphql";

import styles from "./css/Identity.module.css";

interface Props {
	feature: FeatureValues;
	viewer: Viewer;
	children: (props: object, icon: React.ReactNode | null, paywalled: boolean) => React.ReactNode;
	iconInheritColor?: boolean;
	/** Allows access to the paywalled feature ignoring if the user should normally have access */
	allowAccess?: boolean;
	deprecated?: boolean;
}

export function UpgradeIcon({
	className,
	inheritColor = false,
	...rest
}: Omit<FontAwesomeIconProps, "icon"> & { inheritColor?: boolean }) {
	return (
		<FontAwesomeIcon
			icon={faArrowAltCircleUp}
			className={cx(styles.upgradeIcon, className, { [styles.inherit]: inheritColor })}
			{...rest}
		/>
	);
}

const BillingFeaturesPaywallFragment = graphql`
	fragment BillingIdentityFeaturesPaywall_viewer on Viewer {
		account {
			billingAccount {
				featureAccess
			}
		}
	}
`;

/**
 * This component uses the `Render Props` technique. See: https://reactjs.org/docs/render-props.html.
 *
 * Particularly, `Children Prop`, so we can also avoid the `eslint-disable-next-line react/jsx-no-bind` line
 * using this component.
 */

class Identity extends React.Component<Props> {
	static defaultProps: Partial<Props> = {
		iconInheritColor: false,
		allowAccess: false,
		deprecated: false,
	};

	static Fragments = {
		viewer: BillingFeaturesPaywallFragment,
	};

	onClick = () => {
		const { feature } = this.props;

		showFeatureModal(feature);
	};

	render() {
		const {
			viewer: { account },
			children,
			iconInheritColor,
			feature,
			allowAccess,
			deprecated,
		} = this.props;
		// If the workspace doesn't have access to a deprecated feature, we want to hide it completely.
		// The main reason behind this is to prevent users from seeing a feature that we want to stop supporting.
		if (!account.billingAccount.featureAccess[feature] && deprecated) return null;

		if (account.billingAccount.featureAccess[feature] || allowAccess) return children({}, null, false);

		const icon = <UpgradeIcon inheritColor={iconInheritColor} />;

		return children({ onClick: this.onClick }, icon, true);
	}
}

export default smart(Identity);

export function useBillingPaywall(
	feature: FeatureValues,
	viewer: BillingIdentityFeaturesPaywall_viewer$key,
	/** Allows access to the paywalled feature ignoring if the user should normally have access */
	allowAccess?: boolean,
	deprecated?: boolean,
) {
	const {
		account: { billingAccount },
	} = useFragment<BillingIdentityFeaturesPaywall_viewer$key>(BillingFeaturesPaywallFragment, viewer);

	const showPaywall = React.useCallback(() => {
		showFeatureModal(feature);
	}, [feature]);

	const isPaywalled = React.useMemo(() => {
		if (!billingAccount.featureAccess[feature] && deprecated) return false;
		if (billingAccount.featureAccess[feature] || allowAccess) return false;
		return true;
	}, [billingAccount.featureAccess, feature, allowAccess, deprecated]);

	return { isPaywalled, showPaywall };
}
